<template>
  <v-container fluid class="transparent">

    <!-- HEADER -->
    <v-row justify="center" class="my-6 search-area">
      <v-col cols="12" md="10" lg="8">

        <div class="text-center my-8">
          <div class="text-h5 font-weight-bold " style="color: #0066cc">
            Phosphosite Function — Powered by Integrative Intelligence
          </div>

          <div class="text-subtitle-1 mt-3">
            PTMax combines AI-based literature mining with multi-omics
            integration to deliver site-level functional insight at scale
          </div>
        </div>

        <!-- SEARCH AREA -->
        <v-row dense>

          <!-- Gene Search -->
          <v-col cols="12" md="4">
            <v-card outlined class="pa-2 fill-height">

              <div class="card-title">
                <img src="/img/gene.svg"/>
                Gene Search
              </div>

              <v-autocomplete
                  v-model="geneInput"
                  :items="geneSuggestions"
                  :loading="loading"
                  :search-input.sync="geneSearchText"
                  dense
                  hide-details
                  outlined
                  clearable
                  item-text="text"
                  item-value="value"
                  label="Substrate (Gene symbol / UniProt / Ensembl)"
                  class="mx-6"
                  @update:search-input="onGeneSearch"
                  @change="submitGeneSearch"
              />

              <div class="examples">
                <span class="label">Examples:</span>

                <a
                    v-for="item in geneExamples"
                    :key="item"
                    class="example-link"
                    @click="submitGeneSearchExample(item)"
                >
                  {{ item }}
                </a>

              </div>

            </v-card>
          </v-col>


          <!-- Site Search -->
          <v-col cols="12" md="4">
            <v-card outlined class="pa-2 fill-height">

              <div class="card-title">
                <img src="/img/site.svg"/>
                Site Search
              </div>

              <v-autocomplete
                  v-model="siteInput"
                  :items="siteSuggestions"
                  :loading="loading"
                  :search-input.sync="siteSearchText"
                  dense
                  hide-details
                  outlined
                  clearable
                  item-text="text"
                  item-value="value"
                  label="Substrate:site"
                  class="mx-6"
                  @update:search-input="onSiteSearch"
                  @change="submitSiteSearch"
              />

              <div class="examples">
                <span class="label">Examples:</span>

                <a
                    v-for="item in siteExamples"
                    :key="item"
                    class="example-link"
                    @click="submitSiteSearchExample(item)"
                >
                  {{ item }}
                </a>

              </div>

            </v-card>
          </v-col>


          <!-- Multiple Sites -->
          <v-col cols="12" md="4">
            <v-card outlined class="pa-2 fill-height">

              <div class="card-title">
                <img src="/img/multiple-site.svg" class="multi-icon"/>
                Multiple Sites Search
              </div>

              <v-textarea
                  v-model="multiSiteInput"
                  outlined
                  dense
                  hide-details
                  rows="3"
                  class="mx-2 mt-1"
                  label="Substrate, site, and optional value"
              />

              <div class="examples">
                <span class="label">Examples:</span>

                <v-btn
                    v-for="i in 3"
                    :key="i"
                    x-small
                    outlined
                    class="example-btn"
                    @click="loadExample(i)"
                >
                  {{ i }}
                </v-btn>

              </div>

              <div class="text-center mt-2">
                <v-btn
                    color="primary"
                    x-small
                    @click="submitMultiSiteSearch"
                    class="text-capitalize"
                >
                  Search
                </v-btn>
              </div>

            </v-card>
          </v-col>

        </v-row>


        <!-- CAROUSEL -->
        <v-row class="mt-6" justify="center">
          <v-col cols="12" lg="10">

            <v-carousel
                cycle
                progress
                height="auto"
            >

              <v-carousel-item
                  v-for="i in 7"
                  :key="i"
              >
                <v-img
                    :src="`/img/fp${i}.png`"
                    contain
                    aspect-ratio="2.2"
                />
              </v-carousel-item>

            </v-carousel>

          </v-col>
        </v-row>


        <!-- FOOTER -->
        <v-divider class="my-4"></v-divider>

        <v-row justify="center">
          <v-col cols="12" md="8" class="text-center text-caption">

            <v-icon small class="mr-1">
              mdi-information-outline
            </v-icon>

            To get the best experience, please use the latest version of
            Chrome, Edge, or Firefox.

          </v-col>
        </v-row>

      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import Axios from "axios";
import _ from "lodash";
import { examples } from "@/config/exampleList";
import { apiprefix } from "@/main";

export default {

  data() {
    return {

      geneInput: "",
      geneSearchText: "",
      geneSuggestions: [],

      siteInput: "",
      siteSearchText: "",
      siteSuggestions: [],

      multiSiteInput: "",

      loading: false,

      geneExamples: ["MAPK3","P27361","ENSP00000367263"],
      siteExamples: ["MAPK3:Y204","P27361:Y204","ENSP00000367263:S135"]

    };
  },

  methods: {

    loadExample(id){
      this.multiSiteInput = examples[id] || "";
    },

    submitGeneSearchExample(item){
      this.geneInput = item;
      this.submitGeneSearch();
    },

    submitSiteSearchExample(item){
      this.siteInput = item;
      this.submitSiteSearch();
    },

    onGeneSearch: _.debounce(function(val){

      const input = (val || "").trim();

      if(input.length < 2){
        this.geneSuggestions = [];
        return;
      }

      Axios.get(`${apiprefix}/api/gene_suggestions?query=${input}`)
          .then(res => {

            const genes = res.data.genes || [];

            this.geneSuggestions = genes.map(g => ({
              text:g,
              value:g
            }));

          });

    },200),

    onSiteSearch: _.debounce(function(val){

      const input = (val || "").trim();

      if(!input.includes(":")){
        this.siteSuggestions = [];
        return;
      }

      const [gene,partialSite] = input.split(":");

      Axios.get(`${apiprefix}/api/site_suggestions?gene=${gene}`)
          .then(res => {

            const sites = res.data.sites || [];

            this.siteSuggestions = sites
                .filter(s => s.includes(partialSite.toUpperCase()))
                .map(s => ({
                  text:`${gene}:${s}`,
                  value:`${gene}:${s}`
                }));

          });

    },200),

    submitGeneSearch(){

      if(!this.geneInput) return;

      this.$router.push({
        path:"/single-search",
        query:{
          item:this.geneInput.toUpperCase(),
          ptm_type:"phosphorylation"
        }
      });

    },

    submitSiteSearch(){

      if(!this.siteInput) return;

      this.$router.push({
        path:"/single-search",
        query:{
          item:this.siteInput.toUpperCase(),
          ptm_type:"phosphorylation"
        }
      });

    },

    submitMultiSiteSearch(){

      const input = this.multiSiteInput.trim();
      if(!input) return;

      const formatted = input
          .split(/\n+/)
          .map(l=>l.trim())
          .filter(Boolean)
          .map(l=>{
            const p = l.split(/\s+/);
            return `${p[0]}_${p[1]}`;
          });

      this.$router.push({
        path:"/batch-search",
        query:{
          input:input,
          formatted:JSON.stringify(formatted)
        }
      });

    }

  }

};
</script>

<style scoped>

.card-title{
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight:600;
  margin-bottom:10px;
}

.card-title img{
  width:40px;
  height:40px;
  margin-right:8px;
}

.multi-icon{
  width:55px;
  height:40px;
}

.examples{
  display:flex;
  justify-content:center;
  align-items:center;
  flex-wrap:wrap;
  gap:10px;
  margin-top:6px;
  font-size:0.8rem;
}

.label{
  margin-right:4px;
}

.example-link{
  white-space:nowrap;
  cursor:pointer;
  text-decoration:underline;
  color:#1976d2;
}

.example-btn{
  min-width:32px;
}
.search-area{
  background:#f9fafc;
  padding:16px;
  border-radius:10px;
}


</style>